import { notifier } from "../../core/notifier/notifier";
import { ScrollLock } from "../../core/scroll-lock";
import { translator } from "../../translation/translator";
import { spinner } from "../../util/spinner/spinner";
import { ModalNestedPosition } from "../../util/modal-nested-position";
import { PointHistoryApi } from "../point-history/point-history-api";

interface PointHistoryAdminFormLike {
    getForm(): HTMLFormElement | null;
    getModeInput(): HTMLInputElement | null;
    getHistoryIdInput(): HTMLInputElement | null;
    getAmountField(): Element | null;
    getAmountInput(): HTMLInputElement | null;
    getRuleSelectField(): HTMLElement | null;
    getRulePicker(): HTMLElement | null;
    getRuleReadonlyInput(): HTMLInputElement | null;
    getRuleTypeInputs(): HTMLInputElement[];
    getAmountLabel(): HTMLElement | null;
    getRuleLabel(): HTMLElement | null;
    getModal(): HTMLElement | null;
    getTitle(): HTMLElement | null;
    getCheckedRuleTypeInput(): HTMLInputElement | null;
    getNoteInput(): HTMLInputElement | HTMLTextAreaElement | null;
    getSubmitButton(): HTMLButtonElement | null;
    getInvalidateButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
}

interface PointHistoryAdminOptions {
    userId: number;
    onReload: () => Promise<void>;
}

interface InvalidateDetails {
    pointHistoryId: number;
    amountText?: string;
    ruleName?: string;
}

export class PointHistoryAdminHandler {
    public form: PointHistoryAdminFormLike;

    public userId: number;

    public onReload: () => Promise<void>;

    public lastValidAmountValue: string;

    public constructor(form: PointHistoryAdminFormLike, options: PointHistoryAdminOptions) {
        this.form = form;
        this.userId = Number(options?.userId || 0);
        this.onReload = typeof options?.onReload === "function" ? options.onReload : async () => {};
        this.lastValidAmountValue = "";
    }

    private setLabelRequiredState(label: HTMLElement | null, isRequired: boolean): void {
        if (!(label instanceof HTMLElement)) {
            return;
        }

        label.classList.toggle("required", isRequired);
        if (isRequired) {
            label.setAttribute("data-required", "1");
            return;
        }

        label.removeAttribute("data-required");
    }

    public resetModalState(): void {
        const formEl = this.form.getForm();
        const modeEl = this.form.getModeInput();
        const historyIdEl = this.form.getHistoryIdInput();
        const amountField = this.form.getAmountField();
        const amountEl = this.form.getAmountInput();
        const ruleField = this.form.getRuleSelectField();
        const rulePicker = this.form.getRulePicker();
        const ruleReadonlyInput = this.form.getRuleReadonlyInput();
        const ruleInputs = this.form.getRuleTypeInputs();
        const amountLabel = this.form.getAmountLabel();
        const ruleLabel = this.form.getRuleLabel();

        if (formEl instanceof HTMLFormElement) {
            formEl.reset();
        }
        if (modeEl instanceof HTMLInputElement) {
            modeEl.value = "adjust";
        }
        if (historyIdEl instanceof HTMLInputElement) {
            historyIdEl.value = "";
        }
        if (amountField instanceof HTMLElement) {
            amountField.hidden = false;
        }
        if (amountEl instanceof HTMLInputElement) {
            amountEl.disabled = false;
            amountEl.setAttribute("data-validation", "required");
            amountEl.removeAttribute("required");
            amountEl.readOnly = false;
            amountEl.value = "";
            amountEl.setCustomValidity("");
        }
        if (ruleField instanceof HTMLElement) {
            ruleField.hidden = false;
        }
        if (rulePicker instanceof HTMLElement) {
            rulePicker.hidden = false;
        }
        if (ruleReadonlyInput instanceof HTMLInputElement) {
            ruleReadonlyInput.hidden = true;
            ruleReadonlyInput.value = "";
        }
        if (amountLabel instanceof HTMLElement) {
            this.setLabelRequiredState(amountLabel, true);
            amountLabel.setAttribute("data-translate", "point_history_amount");
            amountLabel.textContent = translator.getTranslation("point_history_amount") || "Amount";
        }
        if (ruleLabel instanceof HTMLElement) {
            this.setLabelRequiredState(ruleLabel, true);
            ruleLabel.setAttribute("data-translate", "point_history_rule");
            ruleLabel.textContent = translator.getTranslation("point_history_rule") || "Rule";
        }
        ruleInputs.forEach((ruleInput) => {
            if (ruleInput instanceof HTMLInputElement) {
                ruleInput.disabled = false;
                ruleInput.setAttribute("data-validation", "required");
                ruleInput.removeAttribute("required");
            }
        });
        this.lastValidAmountValue = "";
    }

    public showModal(): void {
        const modal = this.form.getModal();
        if (!(modal instanceof HTMLElement)) return;
        ModalNestedPosition.openModal({ modalEl: modal, displayValue: "block" });
        ScrollLock.lock();
    }

    public closeModal(): void {
        const modal = this.form.getModal();
        if (!(modal instanceof HTMLElement)) return;
        ModalNestedPosition.closeModal({ modalEl: modal });
        ScrollLock.unlock();
        this.resetModalState();
    }

    public openAdjustModal(): void {
        const titleEl = this.form.getTitle();
        this.resetModalState();
        if (titleEl instanceof HTMLElement) {
            titleEl.textContent = translator.getTranslation("point_history_add_points") || "Add points";
        }
        this.showModal();
    }

    public openInvalidateModal(details: InvalidateDetails): void {
        const titleEl = this.form.getTitle();
        const modeEl = this.form.getModeInput();
        const historyIdEl = this.form.getHistoryIdInput();
        const amountEl = this.form.getAmountInput();
        const ruleField = this.form.getRuleSelectField();
        const rulePicker = this.form.getRulePicker();
        const ruleReadonlyInput = this.form.getRuleReadonlyInput();
        const ruleInputs = this.form.getRuleTypeInputs();
        const amountLabel = this.form.getAmountLabel();
        const ruleLabel = this.form.getRuleLabel();

        this.resetModalState();
        if (titleEl instanceof HTMLElement) {
            titleEl.textContent = translator.getTranslation("point_history_invalidate_points") || "Invalidate points";
        }
        if (modeEl instanceof HTMLInputElement) {
            modeEl.value = "invalidate";
        }
        if (historyIdEl instanceof HTMLInputElement) {
            historyIdEl.value = String(details?.pointHistoryId || "");
        }
        if (amountEl instanceof HTMLInputElement) {
            amountEl.disabled = false;
            amountEl.removeAttribute("data-validation");
            amountEl.removeAttribute("required");
            amountEl.readOnly = true;
            amountEl.value = details?.amountText || "";
        }
        if (amountLabel instanceof HTMLElement) {
            this.setLabelRequiredState(amountLabel, false);
            amountLabel.removeAttribute("data-translate");
            amountLabel.textContent = translator.getTranslation("point_history_amount") || "Amount";
        }
        if (ruleField instanceof HTMLElement) {
            ruleField.hidden = false;
        }
        if (rulePicker instanceof HTMLElement) {
            rulePicker.hidden = true;
        }
        if (ruleReadonlyInput instanceof HTMLInputElement) {
            ruleReadonlyInput.hidden = false;
            ruleReadonlyInput.value = details?.ruleName || "";
            ruleReadonlyInput.readOnly = true;
        }
        if (ruleLabel instanceof HTMLElement) {
            this.setLabelRequiredState(ruleLabel, false);
            ruleLabel.setAttribute("data-translate", "point_history_rule");
            ruleLabel.textContent = translator.getTranslation("point_history_rule") || "Rule";
        }
        ruleInputs.forEach((ruleInput) => {
            if (ruleInput instanceof HTMLInputElement) {
                ruleInput.disabled = true;
                ruleInput.removeAttribute("data-validation");
                ruleInput.removeAttribute("required");
                ruleInput.checked = false;
            }
        });
        this.showModal();
    }

    public isPartialSignedNumber(value: string): boolean {
        return value === "" || value === "-" || /^-?(?:\d+)?(?:\.\d*)?$/.test(value);
    }

    public handleAmountKeydown(event: KeyboardEvent): void {
        const amountEl = this.form.getAmountInput();
        if (!(amountEl instanceof HTMLInputElement)) return;
        if (event.ctrlKey || event.metaKey || event.altKey) return;

        const key = event.key;
        const allowedKeys = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Tab",
            "Home",
            "End",
            "Enter",
        ];
        if (allowedKeys.includes(key)) return;

        if (/^\d$/.test(key)) return;

        if (key === ".") {
            if (amountEl.value.includes(".")) {
                event.preventDefault();
            }
            return;
        }

        if (key === "-") {
            const selectionStart = amountEl.selectionStart ?? 0;
            const selectionEnd = amountEl.selectionEnd ?? 0;
            const hasMinusOutsideSelection =
                amountEl.value.includes("-") && !(selectionStart === 0 && selectionEnd > 0);
            if (selectionStart !== 0 || hasMinusOutsideSelection) {
                event.preventDefault();
            }
            return;
        }

        event.preventDefault();
    }

    public handleAmountPaste(event: ClipboardEvent): void {
        const pastedText = event.clipboardData?.getData("text") ?? "";
        if (!this.isPartialSignedNumber(pastedText.trim())) {
            event.preventDefault();
        }
    }

    public handleAmountInput(): void {
        const amountEl = this.form.getAmountInput();
        if (!(amountEl instanceof HTMLInputElement)) return;

        const nextValue = amountEl.value.trim();
        if (this.isPartialSignedNumber(nextValue)) {
            this.lastValidAmountValue = nextValue;
            amountEl.setCustomValidity("");
            return;
        }

        amountEl.value = this.lastValidAmountValue;
        amountEl.setCustomValidity("Amount must be a non-zero number");
    }

    public async handleSubmit(event: SubmitEvent): Promise<void> {
        event.preventDefault();

        const modeEl = this.form.getModeInput();
        const historyIdEl = this.form.getHistoryIdInput();
        const amountEl = this.form.getAmountInput();
        const checkedRuleInput = this.form.getCheckedRuleTypeInput();
        const noteEl = this.form.getNoteInput();

        const mode = modeEl instanceof HTMLInputElement ? modeEl.value : "adjust";
        const pointHistoryId = historyIdEl instanceof HTMLInputElement ? Number(historyIdEl.value || 0) : 0;
        const ruleType =
            mode === "invalidate"
                ? "Invalidate point history"
                : checkedRuleInput instanceof HTMLInputElement
                  ? checkedRuleInput.value.trim()
                  : "";
        const note = noteEl instanceof HTMLTextAreaElement ? noteEl.value.trim() : "";
        const amountRaw = amountEl instanceof HTMLInputElement ? amountEl.value.trim() : "";
        const amount = /^-?(?:\d+)(?:\.\d+)?$/.test(amountRaw) ? Number.parseFloat(amountRaw) : Number.NaN;
        const submitBtn = this.form.getSubmitButton();

        if (ruleType === "") {
            notifier.error("point_history_rule_required");
            return;
        }

        try {
            if (submitBtn instanceof HTMLButtonElement) {
                spinner.showButtonLoader(submitBtn, { translateKey: "spinner_proccesing" });
            }

            if (mode === "invalidate") {
                if (!Number.isFinite(pointHistoryId) || pointHistoryId <= 0) {
                    notifier.error("point_history_id_invalid");
                    return;
                }
                await PointHistoryApi.invalidatePointHistory(pointHistoryId, { reason: note });
                await this.onReload();
                notifier.success("point_history_invalidated_success");
            } else {
                if (!Number.isFinite(amount) || amount === 0) {
                    notifier.error("point_history_amount_non_zero_required");
                    return;
                }
                await PointHistoryApi.adjustPoints(this.userId, {
                    amount,
                    rule_type: ruleType,
                    note,
                });
                await this.onReload();
                notifier.success("point_history_adjusted_success");
            }

            this.closeModal();
        } catch (_error) {
            notifier.error("point_history_update_failed");
        } finally {
            if (submitBtn instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(submitBtn);
            }
        }
    }

    public handleInvalidateButton(invalidateBtn: HTMLButtonElement): void {
        const pointHistoryId = Number(invalidateBtn.dataset.pointHistoryInvalidate || 0);
        if (!Number.isFinite(pointHistoryId) || pointHistoryId <= 0) return;

        this.openInvalidateModal({
            pointHistoryId,
            amountText: invalidateBtn.dataset.pointHistoryAmountText || "-",
            ruleName: invalidateBtn.dataset.pointHistoryRuleName || "-",
        });
    }

    public handlePanelClick(event: MouseEvent): void {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const invalidateBtn = this.form.getInvalidateButton(target);
        if (!(invalidateBtn instanceof HTMLButtonElement)) return;

        event.preventDefault();
        event.stopPropagation();
        this.handleInvalidateButton(invalidateBtn);
    }
}
